import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, CheckCircle2, XCircle, UserPlus, UserCircle } from "lucide-react";
import { queryClient } from "@/lib/queryClient";

interface FriendInvitation {
  id: string;
  inviterId: string;
  inviterName: string;
  inviterEmail: string;
  inviteeEmail: string;
  message?: string;
  status: string;
  relationshipType?: "friend" | "family" | string;
}

const registerSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormInput = z.input<typeof registerSchema>;
type RegisterFormData = z.output<typeof registerSchema>;
export function FriendAccessNotice({ relationshipType = "friend" }: { relationshipType?: string }) {
  const label = relationshipType === "family" ? "family relationship" : "friendship";
  return <div className="rounded-lg border p-3 space-y-2" data-testid="friend-invitation-access-notice">
    <p className="text-sm font-medium">Individual shares only</p>
    <p className="text-xs text-muted-foreground">This {label} does not provide blanket access to Notes/Notepad, Goals, Calendar, Tasks, Habits, or Tracker-X. You can access only records that are shared with you individually.</p>
  </div>;
}

export default function AcceptFriendInvitePage() {
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string | null>(null);

  const form = useForm<RegisterFormInput, any, RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    setToken(tokenParam);
  }, []);

  // Check if user is logged in (handle 401 gracefully)
  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["/api/auth/user"],
    queryFn: async () => {
      const response = await fetch('/api/auth/user', { credentials: 'include' });
      if (!response.ok) {
        if (response.status === 401) {
          return null; // Not logged in
        }
        throw new Error('Failed to check auth status');
      }
      return response.json();
    },
    retry: false,
  });

  // Fetch invitation details (public endpoint, works without login)
  const { data: invitation, isLoading: isLoadingInvitation, isError: isInvitationError, error: invitationError } = useQuery<FriendInvitation>({
    queryKey: ["/api/friend-invitations/by-token", token],
    queryFn: async () => {
      const response = await fetch(`/api/friend-invitations/by-token?token=${encodeURIComponent(token || '')}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Invitation not found or has expired');
        }
        throw new Error('Failed to load invitation');
      }
      return response.json();
    },
    enabled: !!token,
    retry: false,
  });

  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const acceptInviteMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      console.log(`[Accept Invitation] Attempting to accept invitation ${invitationId} (attempt ${retryCount + 1})`);
      setAcceptError(null);
      
      const response = await fetch(`/api/friend-invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      // Handle network errors
      if (!response.ok) {
        let errorMessage = 'Failed to accept invitation';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
          console.error(`[Accept Invitation] Server error:`, data);
        } catch (e) {
          console.error(`[Accept Invitation] Failed to parse error response:`, e);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log(`[Accept Invitation] Success:`, data);
      return data;
    },
    onSuccess: () => {
      console.log(`[Accept Invitation] Invitation accepted successfully, redirecting to community...`);
      setAcceptError(null);
      setTimeout(() => {
        setLocation('/community');
      }, 2000);
    },
    onError: (error: Error) => {
      console.error(`[Accept Invitation] Error:`, error.message);
      setAcceptError(error.message);
      setRetryCount(prev => prev + 1);
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: { token: string; firstName: string; lastName: string; password: string }) => {
      const response = await fetch('/api/auth/register-from-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Registration failed');
      }
      return result;
    },
    onSuccess: () => {
      // Invalidate auth query to refresh user state
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      
      // The backend already accepted the invitation, so redirect to community
      setTimeout(() => {
        setLocation('/community');
      }, 2000);
    },
  });

  const handleAccept = () => {
    if (invitation) {
      acceptInviteMutation.mutate(invitation.id);
    }
  };

  const handleLogin = () => {
    // Store token in sessionStorage so we can redirect back here after login
    if (token) {
      sessionStorage.setItem('friendInviteToken', token);
    }
    setLocation('/');
  };

  const handleRegister = (data: RegisterFormData) => {
    if (token) {
      registerMutation.mutate({
        token,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });
    }
  };

  // Check if we just logged in and have a pending invitation
  useEffect(() => {
    const pendingToken = sessionStorage.getItem('friendInviteToken');
    if (pendingToken && currentUser && !token) {
      sessionStorage.removeItem('friendInviteToken');
      setToken(pendingToken);
    }
  }, [currentUser, token]);

  // Loading state
  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-loading">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invalid Invitation Link</CardTitle>
            <CardDescription data-testid="text-error-description">
              No invitation token provided
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setLocation('/')} 
              className="w-full" 
              data-testid="button-go-home"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-loading">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" data-testid="icon-loading" />
            </div>
            <CardTitle data-testid="text-loading-title">Loading Invitation</CardTitle>
            <CardDescription data-testid="text-loading-description">
              Please wait...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isInvitationError || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invitation Not Found</CardTitle>
            <CardDescription data-testid="text-error-description">
              {invitationError instanceof Error ? invitationError.message : "This invitation may have expired or been cancelled"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setLocation('/')} 
              className="w-full" 
              data-testid="button-go-home"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - invitation accepted (either via accept mutation or registration)
  if (acceptInviteMutation.isSuccess || registerMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-success">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" data-testid="icon-success" />
            </div>
            <CardTitle data-testid="text-success-title">
              {registerMutation.isSuccess ? "Welcome to GoalVia!" : "Friend Request Accepted!"}
            </CardTitle>
            <CardDescription data-testid="text-success-description">
              You are now friends with {invitation.inviterName}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
              <AlertDescription className="text-sm text-center">
                {registerMutation.isSuccess 
                  ? "Your account has been created and you can now share goals and tasks!"
                  : "You can now share goals and tasks with each other!"}
              </AlertDescription>
            </Alert>
            <p className="text-sm text-center text-muted-foreground">
              Redirecting to Community...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (acceptInviteMutation.isError || registerMutation.isError) {
    const errorMessage = acceptInviteMutation.error instanceof Error 
      ? acceptInviteMutation.error.message 
      : registerMutation.error instanceof Error
        ? registerMutation.error.message
        : 'An unexpected error occurred';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Unable to Accept Invitation</CardTitle>
            <CardDescription data-testid="text-error-description">
              {retryCount > 1 
                ? `We've tried ${retryCount} times. Please try again or contact support.`
                : 'Something went wrong while accepting the invitation'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive" data-testid="alert-error">
              <AlertDescription data-testid="text-error-message">
                {errorMessage}
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Button 
                onClick={() => {
                  if (invitation) {
                    acceptInviteMutation.reset();
                    acceptInviteMutation.mutate(invitation.id);
                  }
                }} 
                className="w-full"
                disabled={acceptInviteMutation.isPending}
                data-testid="button-retry-accept"
              >
                {acceptInviteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Retry Accept
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.reload()} 
                className="w-full" 
                data-testid="button-reload-page"
              >
                Reload Page
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              If the problem persists, please contact the person who sent you the invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is not logged in - show registration form with option to log in instead
  if (!currentUser) {
    // Show registration form
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-register">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-green-600 dark:text-green-400" data-testid="icon-register" />
            </div>
            <CardTitle data-testid="text-register-title">Create Your Free Account</CardTitle>
            <CardDescription data-testid="text-register-description">
              {invitation.inviterName} has invited you to connect on GoalVia
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FriendAccessNotice />
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-center">
                Creating account for: <strong>{invitation.inviteeEmail}</strong>
              </p>
            </div>

            {invitation.message && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm font-medium mb-2">Message from {invitation.inviterName}:</p>
                <p className="text-sm italic" data-testid="text-invitation-message">
                  "{invitation.message}"
                </p>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleRegister)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="John" 
                            {...field} 
                            data-testid="input-first-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Doe" 
                            {...field} 
                            data-testid="input-last-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="At least 8 characters" 
                          {...field} 
                          data-testid="input-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Confirm your password" 
                          {...field} 
                          data-testid="input-confirm-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit"
                  disabled={registerMutation.isPending || acceptInviteMutation.isPending}
                  className="w-full" 
                  data-testid="button-register"
                >
                  {registerMutation.isPending || acceptInviteMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Account & Accept Invitation
                    </>
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <button 
                  onClick={handleLogin}
                  className="text-primary hover:underline"
                  data-testid="link-login"
                >
                  Log in instead
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is logged in - show invitation details and accept button
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-md w-full" data-testid="card-invitation">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" data-testid="icon-friend-invite" />
          </div>
          <CardTitle data-testid="text-invitation-title">{invitation.relationshipType === "family" ? "Family Member Invitation" : "Friend Invitation"}</CardTitle>
          <CardDescription data-testid="text-invitation-description">
            {invitation.inviterName} wants to connect with you
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FriendAccessNotice relationshipType={invitation.relationshipType} />
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium mb-2">From:</p>
            <p className="text-base font-semibold" data-testid="text-inviter-name">
              {invitation.inviterName}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="text-inviter-email">
              {invitation.inviterEmail}
            </p>
          </div>

          {invitation.message && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium mb-2">Message:</p>
              <p className="text-sm" data-testid="text-invitation-message">
                {invitation.message}
              </p>
            </div>
          )}

          <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <AlertDescription className="text-sm">
              Accepting creates the friendship. Messaging and individually shared records remain separate.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button 
              onClick={() => setLocation('/community')} 
              variant="outline"
              className="flex-1" 
              data-testid="button-decline"
            >
              Not Now
            </Button>
            <Button 
              onClick={handleAccept}
              disabled={acceptInviteMutation.isPending}
              className="flex-1" 
              data-testid="button-accept"
            >
              {acceptInviteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Accept
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
